import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User } from '../../types';

interface UserState {
  loginUser: User | null;
  isUserLogin: boolean;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  loginUser: null,
  isUserLogin: false,
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<User>) => {
      state.loading = false;
      // Normalize user data to support both legacy and new field names
      const user = {
        ...action.payload,
        displayName: action.payload.displayName || action.payload.display_name,
        firstName: action.payload.firstName || action.payload.first_name,
        lastName: action.payload.lastName || action.payload.last_name,
        phoneNumber: action.payload.phoneNumber || action.payload.phone_number,
        photoURL: action.payload.photoURL || action.payload.photo_url || undefined,
        isLocationVisible: action.payload.isLocationVisible ?? action.payload.is_location_visible,
        emailVerified: action.payload.emailVerified ?? action.payload.email_verified,
        created_at: action.payload.created_at || action.payload.created_at,
      };
      state.loginUser = user;
      state.isUserLogin = true;
      state.error = null;
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.isUserLogin = false;
      state.loginUser = null;
    },
    logout: (state) => {
      state.loginUser = null;
      state.isUserLogin = false;
      state.loading = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.loginUser) {
        // Normalize updated user data to support both legacy and new field names
        const updates = {
          ...action.payload,
          displayName: action.payload.displayName || action.payload.display_name,
          firstName: action.payload.firstName || action.payload.first_name,
          lastName: action.payload.lastName || action.payload.last_name,
          phoneNumber: action.payload.phoneNumber || action.payload.phone_number,
          photoURL: action.payload.photoURL || action.payload.photo_url || undefined,
          isLocationVisible: action.payload.isLocationVisible ?? action.payload.is_location_visible,
          emailVerified: action.payload.emailVerified ?? action.payload.email_verified,
          created_at: action.payload.created_at || action.payload.created_at,
        };
        state.loginUser = { ...state.loginUser, ...updates };
      }
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  updateUser,
} = userSlice.actions;

export default userSlice.reducer;